import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentTextIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import type { ChatSource } from '@archie/api-shared';

interface SourceChipsProps {
  sources: ChatSource[];
  highlightedKey?: string | null;
  onSelect?: (key: string) => void;
}

// Must match the server-side key in packages/api/src/utils/chatSources.ts —
// dedup is by source document, not chunk. documentId is intentionally NOT in
// the key so multiple chunks from the same source share a single chip.
const sourceKey = (s: ChatSource): string =>
  s.kind === 'web' ? `web:${s.url}` : `kb:${s.sourceId ?? s.documentId ?? ''}`;

const labelFor = (source: ChatSource): string => {
  if (source.kind === 'web') {
    if (source.title && source.title.length > 0) return source.title;
    try {
      return new URL(source.url).hostname;
    } catch {
      return source.url;
    }
  }
  // KB chip — server enriches `name` from the sources table when available;
  // fall back to truncated identifiers for older messages or missing rows.
  if (source.name && source.name.length > 0) return source.name;
  return source.sourceId
    ? `Source ${source.sourceId.slice(0, 8)}`
    : source.documentId
      ? `Doc ${source.documentId.slice(0, 8)}`
      : 'Knowledge base';
};

const titleFor = (source: ChatSource): string => {
  if (source.kind === 'web') return source.url;
  if (source.snippet) return source.snippet;
  return labelFor(source);
};

const Chip: React.FC<{
  source: ChatSource;
  index: number;
  highlighted: boolean;
  onSelect?: (key: string) => void;
}> = ({ source, index, highlighted, onSelect }) => {
  const navigate = useNavigate();
  const label = labelFor(source);
  const title = titleFor(source);
  const Icon = source.kind === 'web' ? GlobeAltIcon : DocumentTextIcon;
  const key = sourceKey(source);

  const baseClass =
    'group inline-flex items-center gap-1.5 max-w-[260px] rounded-full px-2.5 py-1 text-xs ' +
    'border transition-colors cursor-pointer';
  const stateClass = highlighted
    ? 'bg-(--color-surface-default) border-(--color-accent) ring-1 ring-(--color-accent) text-(--color-text-primary)'
    : 'bg-(--color-surface-subtle) border-(--color-border-default) text-(--color-text-secondary) hover:bg-(--color-surface-default)';

  const handleClick = (e: React.MouseEvent) => {
    // Modifier-key (Cmd/Ctrl) opens the source directly; default click
    // routes selection to the panel for in-context preview.
    const openDirectly = e.metaKey || e.ctrlKey;
    if (openDirectly) {
      if (source.kind === 'web') {
        window.open(source.url, '_blank', 'noopener,noreferrer');
      } else if (source.kbId) {
        const params = source.sourceId ? `?highlight=${encodeURIComponent(source.sourceId)}` : '';
        navigate(`/organization/knowledge-bases/${source.kbId}/sources${params}`);
      }
      return;
    }
    onSelect?.(key);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`${baseClass} ${stateClass}`}
      title={title}
      aria-label={`Source ${index + 1}: ${label}`}
      aria-pressed={highlighted}
    >
      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-(--color-border-default) text-[10px] font-semibold text-(--color-text-muted) shrink-0">
        {index + 1}
      </span>
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );
};

const SourceChips: React.FC<SourceChipsProps> = ({ sources, highlightedKey, onSelect }) => {
  if (!sources || sources.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Sources">
      {sources.map((source, i) => {
        const key = sourceKey(source);
        return (
          <Chip
            key={`${source.kind}-${i}`}
            source={source}
            index={i}
            highlighted={highlightedKey === key}
            onSelect={onSelect}
          />
        );
      })}
    </div>
  );
};

export default SourceChips;
